import React, { useMemo } from 'react'
import { Box } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useNavigate } from 'react-router-dom'
import { Avatar, Button, Reveal, SectionHead } from '@/design-system'
import { useDiscoverFeed, type DiscoverFeedItem } from '@/features/discover/hooks/useDiscoverFeed'
import { resolveGalleryTint } from '@/features/discover/utils/galleryTint'
import { avatarGradient } from '@/features/connections/utils'
import { Sec } from './SectionShell'

const SHOWCASE_COUNT = 6

const ShowcaseGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(3, 1fr)',
  gap: 'clamp(16px, 2.4vw, 28px)',
  paddingTop: 44,
  '@media (max-width: 900px)': { gridTemplateColumns: 'repeat(2, 1fr)' },
  '@media (max-width: 560px)': { gridTemplateColumns: '1fr' },
})

const ShowcaseCard = styled(Box)({
  position: 'relative',
  borderRadius: 'var(--ds-radius-xl)',
  overflow: 'hidden',
  aspectRatio: '4 / 4.7',
  cursor: 'pointer',
  boxShadow: 'var(--ds-shadow-deep)',
  transition: 'transform var(--ds-duration-normal) var(--ds-ease-out), box-shadow var(--ds-duration-normal) var(--ds-ease)',
  '&:hover': { transform: 'translateY(-6px)', boxShadow: 'var(--ds-shadow-lift)' },
  '&:hover .sc-img': { transform: 'scale(1.05)' },
})

const ShowcaseImg = styled('img')({
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  transition: 'transform 650ms var(--ds-ease)',
})

const ShowcaseTint = styled(Box, {
  shouldForwardProp: (p) => p !== '$wall' && p !== '$floor',
})<{ $wall: string; $floor: string }>(({ $wall, $floor }) => ({
  position: 'absolute',
  inset: 0,
  background: `linear-gradient(165deg, ${$wall} 0%, ${$floor} 100%)`,
}))

const RoomGlyph = styled(Box)({
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  fontSize: 46,
  color: 'rgba(255,255,255,0.22)',
})

const ShowcaseVeil = styled(Box)({
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(180deg, rgba(10,9,14,0.02) 0%, rgba(10,9,14,0.18) 42%, rgba(10,9,14,0.92) 100%)',
})

const KindTag = styled(Box, {
  shouldForwardProp: (p) => p !== '$gallery',
})<{ $gallery?: boolean }>(({ $gallery }) => ({
  position: 'absolute',
  left: 16,
  top: 16,
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9,
  letterSpacing: '0.13em',
  fontWeight: 700,
  color: $gallery ? '#05323b' : '#4a3510',
  background: $gallery ? '#01dafe' : '#ffcf7d',
  padding: '5px 11px',
  borderRadius: 'var(--ds-radius-pill)',
}))

const ShowcaseCaption = styled(Box)({
  position: 'absolute',
  left: 18,
  right: 18,
  bottom: 18,
  color: '#fff',
})

const ShowcaseTitle = styled(Box)({
  fontSize: 19,
  fontWeight: 740,
  letterSpacing: '-0.02em',
  lineHeight: 1.15,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const ShowcaseOwnerRow = styled(Box)({
  marginTop: 10,
  display: 'flex',
  alignItems: 'center',
  gap: 8,
})

const ShowcaseMeta = styled(Box)({
  fontSize: 12,
  color: 'rgba(255,255,255,0.75)',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

const ShowcaseFoot = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 14,
  paddingTop: 32,
})

const ShowcaseNote = styled(Box)({
  fontFamily: 'var(--ds-font-mono)',
  fontSize: 9,
  letterSpacing: '0.12em',
  color: 'var(--ds-ink-subtle)',
})

const SkelCard = styled(Box)({
  position: 'relative',
  borderRadius: 'var(--ds-radius-xl)',
  aspectRatio: '4 / 4.7',
  background: 'var(--ds-hover)',
})

function itemMeta(item: DiscoverFeedItem): string {
  if (item.kind === 'album' && item.album) {
    const n = item.album.photoCount
    return `${n} photo${n === 1 ? '' : 's'}`
  }
  return 'Walkable room'
}

function itemCover(item: DiscoverFeedItem): string | undefined {
  if (item.kind === 'album') return item.album?.coverUrl
  return item.gallery?.arts?.[0]?.img
}

/**
 * A single showcase tile — full-bleed cover photo (or a real room-tint
 * gradient fallback for galleries whose scene hasn't loaded yet), a dark
 * veil, and white caption text. No white card chrome by design: this is
 * the marketing pitch, not the /discover app grid.
 */
function ShowcaseTile({ item, delay, onOpen }: { item: DiscoverFeedItem; delay: number; onOpen: (item: DiscoverFeedItem) => void }) {
  const summary = item.kind === 'album' ? item.album : item.gallery
  if (!summary) return null

  const isGallery = item.kind === 'gallery'
  const cover = itemCover(item)
  const tint = isGallery && item.gallery ? resolveGalleryTint(item.gallery) : undefined
  const owner = summary.owner

  return (
    <Reveal delay={delay}>
      <ShowcaseCard onClick={() => onOpen(item)}>
        {cover ? (
          <ShowcaseImg className='sc-img' src={cover} alt='' loading='lazy' />
        ) : tint ? (
          <>
            <ShowcaseTint $wall={tint.wallColor} $floor={tint.floorColor} />
            <RoomGlyph>◈</RoomGlyph>
          </>
        ) : null}
        <ShowcaseVeil />
        <KindTag $gallery={isGallery}>{isGallery ? '◈ 3D GALLERY' : 'ALBUM'}</KindTag>
        <ShowcaseCaption>
          <ShowcaseTitle>{summary.title}</ShowcaseTitle>
          <ShowcaseOwnerRow>
            {owner && (
              <Avatar sx={{ width: 22, height: 22, fontSize: 10, background: avatarGradient(owner.name), color: '#fff', flexShrink: 0 }}>
                {owner.name?.[0]?.toUpperCase()}
              </Avatar>
            )}
            <ShowcaseMeta>
              {owner ? `${owner.name} · ` : ''}
              {itemMeta(item)}
            </ShowcaseMeta>
          </ShowcaseOwnerRow>
        </ShowcaseCaption>
      </ShowcaseCard>
    </Reveal>
  )
}

/**
 * Real public albums & 3D galleries, straight from `useDiscoverFeed()` — the
 * same unauthenticated hook /discover uses, mixed so both kinds are
 * represented rather than however the feed happens to be ordered. A
 * bespoke, full-bleed editorial card (no white chrome) instead of reusing
 * /discover's own grid — this is the marketing pitch, not the app. No
 * sample data: if nothing has been shared publicly yet, the section
 * renders nothing rather than fake it.
 */
export function AlreadySharedSection() {
  const navigate = useNavigate()
  const { albums, galleries, loading } = useDiscoverFeed()

  const preview = useMemo<DiscoverFeedItem[]>(() => {
    const albumItems: DiscoverFeedItem[] = albums.map((a) => ({ id: a.id, kind: 'album', album: a, likes: a.likes || 0 }))
    const galleryItems: DiscoverFeedItem[] = galleries.map((g) => ({ id: g.id, kind: 'gallery', gallery: g, likes: g.likes || 0 }))
    const mixed: DiscoverFeedItem[] = []
    const max = Math.max(albumItems.length, galleryItems.length)
    for (let i = 0; i < max && mixed.length < SHOWCASE_COUNT; i++) {
      if (albumItems[i]) mixed.push(albumItems[i])
      if (galleryItems[i] && mixed.length < SHOWCASE_COUNT) mixed.push(galleryItems[i])
    }
    return mixed
  }, [albums, galleries])

  if (!loading && preview.length === 0) return null

  const openItem = (item: DiscoverFeedItem) => {
    if (item.kind === 'album' && item.album) navigate(`/a/${item.album.id}`)
    else if (item.kind === 'gallery' && item.gallery) navigate(`/g/${item.gallery.id}`)
  }

  return (
    <Sec id='discover'>
      <Reveal>
        <SectionHead
          index={5}
          title='Already open to everyone.'
          aside={
            <>
              REAL ALBUMS · WALKABLE ROOMS
              <br />
              SHARED PUBLICLY, TODAY
            </>
          }
        />
      </Reveal>
      <ShowcaseGrid>
        {loading
          ? Array.from({ length: SHOWCASE_COUNT }).map((_, i) => <SkelCard key={i} />)
          : preview.map((item, i) => <ShowcaseTile key={item.id} item={item} delay={i * 60} onOpen={openItem} />)}
      </ShowcaseGrid>
      <ShowcaseFoot>
        <Button variant='secondary' size='lg' onClick={() => navigate('/discover')}>
          Browse public albums & galleries →
        </Button>
        <ShowcaseNote>NO ACCOUNT NEEDED TO LOOK</ShowcaseNote>
      </ShowcaseFoot>
    </Sec>
  )
}
